import React, { FC, useEffect, useRef } from 'react';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import { Button, CircularButton, Icon, icons } from '@hoffmann/pattern-library-2';

import { ReactComponent as CompanyLogoBlackSvg } from '../../assets/images/hoffmann-group-black.svg';
import { ReactComponent as CompanyLogoSvg } from '../../assets/images/hoffmann-group-pictorial.svg';
import { ReactComponent as LinkToOtherPageSvg } from '../../assets/images/link-to.svg';
import { withStyledClasses } from '../../styled/withStyledClasses';
import { callIfFunction } from '../../utils/callIfFunction';

export const SideNavigation: FC<any> = ({ onClose }) => {
  const t = useTranslate('SideNavigation');
  const myModalRef = useRef();
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'escape') {
        callIfFunction(onClose);
      }
    };
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [onClose]);

  const handleClickOutside = (e) => {
    if (myModalRef && myModalRef.current) {
      const ref: any = myModalRef.current;
      if (!ref.contains(e.target)) {
        callIfFunction(onClose);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });

  return (
    <SidebarModal ref={myModalRef}>
      <Header>
        <HeadLine>{t('HEADER')}</HeadLine>
        <SideBarCloseButton
          icon={<Icon source={icons.Close} />}
          ghost
          onClick={onClose}
          size={200}
        />
      </Header>
      <Content>
        <ListItemContainer>
          <SidebarButtonWithIcon>
            <SidebarButton
              label={t('E_SHOP')}
              icon={<CompanyLogo />}
              navigation
              href={t('E_SHOP_URL')}
              target="_blank"
              onClick={() => window.open(t('E_SHOP_URL'), '_blank')}
            />
            <LinkToOtherPage
              target="_blank"
              onClick={() => window.open(t('E_SHOP_URL'), '_blank')}
            />
          </SidebarButtonWithIcon>
          <SidebarButtonWithIcon>
            <SidebarButton
              label={t('GO_DIGITAL_STORE')}
              icon={<CompanyLogo />}
              navigation
              href={t('GO_DIGITAL_STORE_URL')}
              target="_blank"
              onClick={() => window.open(t('GO_DIGITAL_STORE_URL'), '_blank')}
            />
            <LinkToOtherPage
              target="_blank"
              onClick={() => window.open(t('GO_DIGITAL_STORE_URL'), '_blank')}
            />
          </SidebarButtonWithIcon>
          <SidebarButtonWithIcon>
            <SidebarButton
              label={t('TOOLSCOUT')}
              icon={<CompanyLogoBlack />}
              navigation
              href={t('TOOLSCOUT_URL')}
              target="_blank"
              onClick={() => window.open(t('TOOLSCOUT_URL'), '_blank')}
            />
            <LinkToOtherPage
              target="_blank"
              onClick={() => window.open(t('TOOLSCOUT_URL'), '_blank')}
            />
          </SidebarButtonWithIcon>
        </ListItemContainer>
      </Content>
    </SidebarModal>
  );
};

const SidebarModal = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  display: block;
  justify-content: right;
  align-items: top;
  z-index: 100;
  background-color: ${({ theme }) => theme.colors.white};
  border-left: 1px solid ${({ theme }) => theme.colors.layout50};
  width: 375px;
  transition: transform 0.3s cubic-bezier(0, 0, 0.3, 1);
  box-shadow: 2px 0 12px rgb(0 0 0 / 40%);
`;

const Header = styled.div`
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.brand100};
  height: 72px;
  font-size: 23px;
  padding: 0 32px;
  position: relative;
  margin: 0;
  justify-content: center;
  width: 100%;
  font-weight: 100;
`;

const HeadLine = styled.div`
  top: calc(72px / 2);
  position: absolute;
  transform: translateY(-50%);
  font-size: 24px;
  font-weight: 300;
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  top: 0;
  left: 0;
`;

const ListItemContainer = styled.div`
  padding: 16px;
`;

const CompanyLogo = styled(CompanyLogoSvg)`
  margin-right: 16px;
  width: 24px;
  height: 24px;
`;

const CompanyLogoBlack = styled(CompanyLogoBlackSvg)`
  margin-right: 16px;
  width: 24px;
  height: 24px;
`;

const SideBarCloseButton = styled(withStyledClasses(CircularButton))`
  position: absolute;
  left: 317px;
  top: 14px;
  color: ${({ theme }) => theme.colors.white};
`;

const SidebarButtonWithIcon = styled.div`
  ${({ theme }) => `
    height: 48px;
    color: ${theme.colors.layout100};
    &:hover {
      background-color: ${theme.colors.layout40};
      color: ${theme.colors.layout100};
    }
  `}
`;

const LinkToOtherPage = styled(LinkToOtherPageSvg)`
  ${SidebarButtonWithIcon}:hover & {
    position: relative;
    top: 20;
    top: -35px;
    size: 24px;
    width: 24px;
    height: 24px;
    left: 289px;
    margin-left: 16px;
    display: block;
    cursor: pointer;
  }
  display: none;
`;

const SidebarButton = styled(withStyledClasses(Button))`
  ${({ theme }) => `
    width: 100%;
    justify-content: left;
    padding-bottom 12px;
    padding-left 16px;
    padding-right 16px;
    padding-top 12px;
    line-height 24px;
    font-size: 16px;
    font-weight: 400;
    height: 48px;
    border: 0;
    color: ${theme.colors.layout100};
    &:hover {
      background-color: ${theme.colors.layout40};
      color: ${theme.colors.layout100};
    }
  `}
`;
